import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ListingVerification from '../components/ListingVerification';
import { useAuth } from '../contexts/AuthContext';
import { apiClient, guidebooksApi } from '../lib/api';
import Guidebook from './Guidebook';

type ViewState =
  | { type: 'loading' }
  | { type: 'error'; message: string }
  | { type: 'no_active_stay'; listingName: string }
  | { type: 'verification'; listingId: number; listingName: string }
  | { type: 'guidebook' };

const LS_KEY_PREFIX = 'guidebook_qr_';

export default function ListingGuidebook() {
  const { listingCode } = useParams<{ listingCode: string }>();
  const { login } = useAuth();
  const [viewState, setViewState] = useState<ViewState>({ type: 'loading' });

  useEffect(() => {
    if (!listingCode) {
      setViewState({ type: 'error', message: 'Invalid link' });
      return;
    }

    (async () => {
      try {
        // Send stored token to backend for server-side validation
        const storedToken = localStorage.getItem(LS_KEY_PREFIX + listingCode) || undefined;
        const status = await guidebooksApi.listingStatus(listingCode, storedToken);

        if (status.status === 'no_active_stay') {
          localStorage.removeItem(LS_KEY_PREFIX + listingCode);
          setViewState({ type: 'no_active_stay', listingName: status.listingName });
          return;
        }

        // Active stay — backend validated the token
        if (status.tokenValid && storedToken) {
          apiClient.setToken(storedToken);
          login(storedToken);
          setViewState({ type: 'guidebook' });
          return;
        }

        // Token invalid/expired/missing — clean up and show verification
        localStorage.removeItem(LS_KEY_PREFIX + listingCode);
        setViewState({
          type: 'verification',
          listingId: status.listingId,
          listingName: status.listingName,
        });
      } catch {
        setViewState({ type: 'error', message: 'Unable to load guidebook. Please try again.' });
      }
    })();
  }, [listingCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVerified = (token: string) => {
    if (!listingCode) return;
    localStorage.setItem(LS_KEY_PREFIX + listingCode, token);
    apiClient.setToken(token);
    login(token);
    setViewState({ type: 'guidebook' });
  };

  if (viewState.type === 'loading') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <p style={{ color: '#64748b' }}>Loading...</p>
      </div>
    );
  }

  if (viewState.type === 'error') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <p style={{ color: '#dc2626' }}>{viewState.message}</p>
      </div>
    );
  }

  if (viewState.type === 'no_active_stay') {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f8fafc',
          padding: '1rem',
        }}
      >
        <div
          style={{
            background: '#fff',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)',
            padding: '2.5rem 2rem',
            width: '100%',
            maxWidth: '400px',
            textAlign: 'center',
          }}
        >
          <h1 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>
            {viewState.listingName}
          </h1>
          <p style={{ color: '#64748b', fontSize: '0.875rem', lineHeight: 1.6 }}>
            No active reservation at this property right now. If you're a guest, please try again after check-in.
          </p>
        </div>
      </div>
    );
  }

  if (viewState.type === 'verification') {
    return (
      <ListingVerification
        listingCode={listingCode!}
        listingName={viewState.listingName}
        onVerified={handleVerified}
      />
    );
  }

  // type === 'guidebook' — authenticated
  return <Guidebook />;
}
